import React from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaLock, FaUserShield, FaFileContract, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const Cybersecurity = ({ market }) => {
  const b = getBrand(market);
  const ctaLink = market === 'lu' ? '/services/assessment' : '/contact';

  // 4 threats → 2×2 grid
  const threats = [
    { title: 'Ransomware Attacks',          description: 'Encrypt your data and demand payment — devastating for small businesses.' },
    { title: 'Phishing & Social Engineering', description: 'Trick employees into revealing passwords or sensitive information.' },
    { title: 'Data Breaches',               description: 'GDPR fines up to €20M or 4% of revenue — compliance is critical.' },
    { title: 'Weak Password Hygiene',        description: 'The most common entry point for attackers targeting SMBs.' }
  ];

  // 4 solutions → 2×2 grid
  const solutions = [
    {
      icon: <FaUserShield size={32} color={b.accent} />,
      title: 'Identity & Access Management',
      description: 'Multi-factor authentication, single sign-on, and password management.',
      features: ['Microsoft Entra ID', 'Okta integration', 'Conditional access policies', 'Password manager setup']
    },
    {
      icon: <FaLock size={32} color={b.accent} />,
      title: 'Endpoint Protection',
      description: 'Protect every device — laptops, phones, tablets — from malware and threats.',
      features: ['Microsoft Defender for Endpoint', 'Antivirus & anti-malware', 'Device encryption', 'Mobile device management']
    },
    {
      icon: <FaFileContract size={32} color={b.accent} />,
      title: 'GDPR Compliance',
      description: market === 'lu' ? 'Luxembourg & EU data protection compliance for small businesses.' : 'End-to-end compliance support for EU data protection requirements.',
      features: ['Data inventory & classification', 'Privacy policy templates', 'Data processing agreements', 'Breach response planning']
    },
    {
      icon: <FaShieldAlt size={32} color={b.accent} />,
      title: 'Security Awareness Training',
      description: 'Your team is your first line of defence — train them to spot threats.',
      features: ['Phishing simulation campaigns', 'Security best practices training', 'Incident reporting procedures', 'Monthly security digest']
    }
  ];

  // 6 essential security items → 3-col grid
  const essentials = [
    'Multi-factor authentication (MFA) on all accounts',
    'Automatic backups with tested recovery',
    'Encrypted data storage and transmission',
    'Regular security updates and patches',
    'Email filtering and anti-phishing tools',
    'Secure password management for all staff'
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title={market === 'lu' ? 'Cybersecurity for Luxembourg Businesses' : 'Cybersecurity Services'}
        subtitle={market === 'lu'
          ? 'Affordable, GDPR-compliant security that protects your business without breaking the bank.'
          : 'Comprehensive security solutions to protect your digital assets and ensure regulatory compliance.'}
        ctaText={market === 'lu' ? 'Get Free Security Assessment' : 'Secure Your Business'}
        ctaLink={ctaLink}
        icon={<FaShieldAlt color="#1A237E" />}
      />

      {/* ── THREATS — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.isMWO ? '#FF6B6B' : '#DC2626' }}>Threat Landscape</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Common security threats
            </h2>
          </div>
          <div className="grid-4">
            {threats.map((t, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                style={{
                  background: b.isMWO ? '#1a0f0f' : '#FFF5F5',
                  borderRadius: b.cardRadius,
                  padding: '28px 24px',
                  borderLeft: b.isMWO ? '3px solid #FF6B6B' : '3px solid #FCA5A5'
                }}
              >
                <FaExclamationTriangle size={24} color={b.isMWO ? '#FF6B6B' : '#DC2626'} style={{ marginBottom: '14px' }} />
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.isMWO ? '#FFD5D5' : '#991B1B', marginBottom: '8px' }}>
                  {t.title}
                </h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.isMWO ? 'rgba(255,213,213,0.65)' : '#6B2121' }}>
                  {t.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTIONS — 4 → 2×2 ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>What We Offer</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our security solutions
            </h2>
          </div>
          <div className="grid-4">
            {solutions.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px' }}
              >
                <div style={{ marginBottom: '16px' }}>{s.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '16px' }}>{s.description}</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {s.features.map((f, j) => (
                    <li key={j} style={{ display: 'flex', gap: '8px', fontSize: b.fz.xs, color: b.textMuted, alignItems: 'flex-start' }}>
                      <span style={{ color: b.accent, flexShrink: 0 }}>→</span> {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ESSENTIALS CHECKLIST — 6 → 3-col ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Baseline Security</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
              Essential security checklist
            </h2>
            <p style={{ color: b.textMuted, maxWidth: '500px', margin: '0 auto', fontSize: b.fz.base }}>
              Every {market === 'lu' ? 'Luxembourg ' : ''}business should have these security basics in place
            </p>
          </div>
          <div className="grid-6">
            {essentials.map((item, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.isMWO ? '#0d1f0f' : '#F0FFF4', borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? '3px solid #4CAF50' : 'none' }}
              >
                <FaCheckCircle size={16} color="#4CAF50" className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6 }}>{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SMB CONTEXT (lu only) ── */}
      {market === 'lu' && (
        <section style={{ background: b.bg, padding: b.secPad }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              Why small businesses are targeted
            </h2>
            <p style={{ fontSize: b.fz.base, lineHeight: 1.8, color: b.textMuted, marginBottom: '20px' }}>
              43% of cyberattacks target small businesses, yet only 14% are prepared. Hackers know SMBs often have limited defences:
            </p>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {[
                'Limited security budgets and in-house expertise',
                'Valuable customer and financial data worth stealing',
                'Fewer security layers than large enterprises',
                'Staff who have not received security awareness training'
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '10px', fontSize: b.fz.base, color: b.textMuted, alignItems: 'flex-start' }}>
                  <span style={{ color: '#DC2626', flexShrink: 0, marginTop: '2px' }}>✕</span> {item}
                </li>
              ))}
            </ul>
            <p style={{ fontSize: b.fz.base, lineHeight: 1.8, color: b.isMWO ? b.accent : '#16A34A', fontWeight: 600 }}>
              The good news? Basic security measures stop 99% of attacks — and they're affordable.
            </p>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Secure your business today"
        body={market === 'lu'
          ? 'Get a free security assessment and discover where your vulnerabilities are.'
          : "Let's discuss your security needs and build a comprehensive protection plan."}
        ctaText="Get Started"
        ctaLink={ctaLink}
      />
    </div>
  );
};

export default Cybersecurity;
